/**
 * src/integrations/api/client.ts
 *
 * JWT tokens stored in localStorage (access + refresh).
 * Tenant-aware: Authorization Bearer + X-Tenant-Host.
 * Integrated SSO refreshes against User Module module-refresh.
 */
import { isIntegratedMode, redirectToPortalLogin } from "./sso";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8004";
const TENANT_HOST_KEY = "erp_tenant_host";

export function getUserServiceUrl(path: string): string {
  const base = (import.meta.env.VITE_USER_SERVICE_URL as string | undefined)?.trim()
    || "http://localhost:8000";
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${base.replace(/\/$/, "")}${clean}`;
}

export function getTenantHost(): string {
  try {
    const suffix = (import.meta.env.VITE_FRONTEND_DOMAIN_SUFFIX as string | undefined)?.replace(/^\./, "") || "";
    const hostname = window.location.hostname;
    if (suffix && (hostname === suffix || hostname.endsWith(`.${suffix}`))) {
      return window.location.host;
    }
    if (hostname.endsWith(".localhost") || hostname.includes(".")) {
      return window.location.host;
    }
  } catch {
    /* ignore */
  }
  try {
    return localStorage.getItem(TENANT_HOST_KEY) || "";
  } catch {
    return "";
  }
}

export function storeTenantHost(host?: string | null) {
  if (!host) return;
  try {
    localStorage.setItem(TENANT_HOST_KEY, host.split("/")[0]);
  } catch {
    /* ignore */
  }
}

// ─── Token storage ────────────────────────────────────────────────────────────
const ACCESS_KEY  = "hams_access_token";
const REFRESH_KEY = "hams_refresh_token";

export const tokenStore = {
  getAccess:  ()      => localStorage.getItem(ACCESS_KEY) || localStorage.getItem("erp_token"),
  getRefresh: ()      => localStorage.getItem(REFRESH_KEY) || localStorage.getItem("erp_refresh_token"),
  set: (access: string, refresh: string) => {
    localStorage.setItem(ACCESS_KEY,  access);
    localStorage.setItem(REFRESH_KEY, refresh);
    localStorage.setItem("erp_token", access);
    localStorage.setItem("erp_refresh_token", refresh);
  },
  clear: () => {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem("erp_token");
    localStorage.removeItem("erp_refresh_token");
  },
};

function isAccessTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (!payload.exp) return false;
    return payload.exp * 1000 < Date.now() + 30_000;
  } catch {
    return true;
  }
}

export async function ensureFreshAccessForSso(access: string, refresh?: string | null): Promise<string> {
  if (!isAccessTokenExpired(access)) return access;
  if (!refresh) return access;
  tokenStore.set(access, refresh);
  const next = await refreshAccessToken();
  return next || access;
}

export async function revokeCentralSession(): Promise<void> {
  const refresh = tokenStore.getRefresh();
  const access = tokenStore.getAccess();
  if (!refresh && !access) return;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 2500);
  try {
    await fetch(getUserServiceUrl("/api/platform/auth/logout/"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh, access }),
      signal: controller.signal,
    });
  } catch {
    /* still clear local session */
  } finally {
    window.clearTimeout(timer);
  }
}

// ─── Core fetch wrapper ───────────────────────────────────────────────────────
let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

export async function refreshAccessToken(): Promise<string | null> {
  const refresh = tokenStore.getRefresh();
  if (!refresh) return null;

  if (isIntegratedMode()) {
    const res = await fetch(getUserServiceUrl("/api/platform/auth/module-refresh/"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh }),
    });
    if (!res.ok) {
      tokenStore.clear();
      return null;
    }
    const data = await res.json();
    const access = data?.data?.access || data?.access;
    if (!access) return null;
    tokenStore.set(access, data?.data?.refresh || data?.refresh || refresh);
    return access;
  }

  const res = await fetch(`${API_BASE}/api/auth/token/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });
  if (!res.ok) {
    tokenStore.clear();
    return null;
  }
  const data = await res.json();
  tokenStore.set(data.access, data.refresh ?? refresh);
  return data.access;
}

export async function apiFetch(
  path: string,
  init: RequestInit = {},
  retry = true
): Promise<Response> {
  const token = tokenStore.getAccess();
  const headers = new Headers(init.headers);
  if (!(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const tenantHost = getTenantHost();
  if (tenantHost && !headers.has("X-Tenant-Host")) {
    headers.set("X-Tenant-Host", tenantHost);
  }

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const sameOrigin = url.startsWith("/") || (
    typeof window !== "undefined" && url.startsWith(window.location.origin)
  );
  const res = await fetch(url, { ...init, headers, credentials: sameOrigin ? "include" : "omit" });

  const isAuthEndpoint = path.includes("/api/auth/login") || path.includes("/api/auth/token/refresh") || path.includes("/api/auth/token-login");
  if (res.status === 401 && retry && !isAuthEndpoint) {
    // Queue concurrent requests during refresh
    if (isRefreshing) {
      return new Promise((resolve) => {
        refreshQueue.push((newToken) => {
          headers.set("Authorization", `Bearer ${newToken}`);
          resolve(fetch(url, { ...init, headers, credentials: sameOrigin ? "include" : "omit" }));
        });
      });
    }
    isRefreshing = true;
    const newToken = await refreshAccessToken();
    isRefreshing = false;

    if (newToken) {
      refreshQueue.forEach((cb) => cb(newToken));
      refreshQueue = [];
      headers.set("Authorization", `Bearer ${newToken}`);
      return fetch(url, { ...init, headers, credentials: sameOrigin ? "include" : "omit" });
    } else {
      tokenStore.clear();
      import("@/store/useAuthStore").then((mod) => {
        mod.useAuthStore.getState().clearAuth();
      });
      if (isIntegratedMode()) {
        redirectToPortalLogin();
      } else {
        window.location.assign("/auth");
      }
      return res;
    }
  }

  return res;
}

// ─── Typed helpers ────────────────────────────────────────────────────────────
export async function apiGet<T>(path: string, params?: Record<string, string | number | boolean | null | undefined>): Promise<T> {
  let fullPath = path;
  if (params) {
    const searchParams = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
      if (v !== null && v !== undefined && v !== "") {
        searchParams.set(k, String(v));
      }
    }
    const qs = searchParams.toString();
    if (qs) {
      fullPath = `${path}${path.includes('?') ? '&' : '?'}${qs}`;
    }
  }
  const res = await apiFetch(fullPath, { method: "GET" });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return res.json();
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const isFd = body instanceof FormData;
  const res = await apiFetch(path, { method: "POST", body: isFd ? body : JSON.stringify(body) });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return res.json();
}

export async function apiPatch<T>(path: string, body: unknown): Promise<T> {
  const isFd = body instanceof FormData;
  const res = await apiFetch(path, { method: "PATCH", body: isFd ? body : JSON.stringify(body) });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return res.json();
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const isFd = body instanceof FormData;
  const res = await apiFetch(path, { method: "PUT", body: isFd ? body : JSON.stringify(body) });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
  return res.json();
}

export async function apiDelete(path: string): Promise<void> {
  const res = await apiFetch(path, { method: "DELETE" });
  if (!res.ok) throw new Error(await extractErrorMessage(res));
}

function getFirstErrorString(obj: unknown, prefix = ''): string | null {
  if (typeof obj === 'string') return prefix ? `${prefix}: ${obj}` : obj;
  if (Array.isArray(obj)) {
    for (const item of obj) {
      const s = getFirstErrorString(item, prefix);
      if (s) return s;
    }
  } else if (typeof obj === 'object' && obj !== null) {
    for (const [key, val] of Object.entries(obj)) {
      const niceKey = key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
      const newPrefix = prefix ? `${prefix} > ${niceKey}` : niceKey;
      const s = getFirstErrorString(val, newPrefix);
      if (s) return s;
    }
  }
  return null;
}

async function extractErrorMessage(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const data = JSON.parse(text);
    if (data.detail) return String(data.detail);
    if (data.non_field_errors) return [].concat(data.non_field_errors).join(", ");
    
    const firstStr = getFirstErrorString(data);
    if (firstStr) return firstStr;
    
    return JSON.stringify(data);
  } catch {
    // If it's an HTML response (e.g., generic 500 page or Django debug page), don't show the raw HTML
    if (text.trim().toLowerCase().startsWith("<!doctype html") || text.trim().toLowerCase().startsWith("<html")) {
      return `Server error (${res.status}): An unexpected error occurred on the server. Please check server logs.`;
    }
  }
  return text || `Server error (${res.status})`;
}

// ─── Paginated list helper ─────────────────────────────────────────────────────
export type DRFPage<T> = {
  count:    number;
  next:     string | null;
  previous: string | null;
  results:  T[];
};

export async function apiList<T>(path: string, params?: Record<string, string | number | boolean | null | undefined>): Promise<DRFPage<T>> {
  const searchParams = new URLSearchParams();
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== null && v !== undefined && v !== "") {
        searchParams.set(k, String(v));
      }
    }
  }
  const qs = searchParams.toString();
  const fullPath = `${path}${qs ? "?" + qs : ""}`;
  const response = await apiGet<any>(fullPath);

  // If DRF returns a paginated dictionary, it must have .results
  if (response && typeof response === "object" && !Array.isArray(response) && Array.isArray(response.results)) {
    return response as DRFPage<T>;
  }

  // If DRF returns a raw array (pagination_class = None)
  if (Array.isArray(response)) {
    return {
      count: response.length,
      next: null,
      previous: null,
      results: response as T[],
    };
  }

  // If neither, fail loudly
  throw new Error(
    `API Shape Mismatch at ${path}: Expected DRF paginated object { results: [...] } or array, but received: ${JSON.stringify(response).slice(0, 100)}...`
  );
}
